import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DiskStatusDto, ResourceDiskDto, SubscriptionDiskDto } from '@cpm/shared';
import { formatLimitAmount, OVERUSE_POLICY_LABELS } from '@cpm/shared';
import { HardDrive, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { formatMb, timeAgo } from './charts.js';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorNote, MeterBar, Spinner } from './ui.js';

/**
 * Disk usage panels (ADR-0013/0014): quota state + per-resource breakdown
 * on the subscription page, and the per-server capacity overview on the
 * Infrastructure page. Unmeasured resources say so — never a zero.
 */

function breakdownSummary(resource: ResourceDiskDto): string {
  const byKind = { volume: 0, bind: 0, layer: 0 };
  for (const item of resource.items) byKind[item.kind] += item.sizeMb;
  const parts: string[] = [];
  if (byKind.volume > 0) parts.push(`volumes ${formatMb(byKind.volume)}`);
  if (byKind.bind > 0) parts.push(`files ${formatMb(byKind.bind)}`);
  if (byKind.layer > 0) parts.push(`containers ${formatMb(byKind.layer)}`);
  return parts.join(' · ');
}

/** Disk usage-vs-quota card for the subscription detail page. */
export function SubscriptionDiskPanel({ subscriptionId }: { subscriptionId: string }) {
  const disk = useQuery({
    queryKey: ['disk', 'subscription', subscriptionId],
    queryFn: () => api.get<SubscriptionDiskDto>(`/api/metrics/disk/subscriptions/${subscriptionId}`),
    refetchInterval: 60_000,
  });

  // The metrics module can be disabled — hide the card entirely then.
  if (disk.error) return null;
  if (disk.isPending) {
    return (
      <Card>
        <CardHeader title="Disk usage" />
        <Spinner />
      </Card>
    );
  }
  const data = disk.data;
  const quota = data.quota;

  return (
    <Card>
      <CardHeader
        title="Disk usage"
        description="Measured on the servers: named volumes, Coolify file storages and container layers"
        actions={
          quota && quota.exceeded ? (
            <Badge tone={quota.flagged ? 'red' : 'yellow'}>
              {quota.flagged ? 'over quota' : 'over limit'}
              {quota.exceededSince ? ` since ${timeAgo(quota.exceededSince)}` : ''}
            </Badge>
          ) : undefined
        }
      />
      {data.resources.length === 0 ? (
        <EmptyState
          icon={<HardDrive className="size-10" />}
          title="No resources to measure"
          description="Disk usage appears once this subscription has resources."
        />
      ) : (
        <div className="space-y-4 px-5 py-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="text-slate-500 dark:text-slate-400">
              Total{data.measuredAt ? ` · measured ${timeAgo(data.measuredAt)}` : ''}
            </span>
            <span className="font-medium tabular-nums">
              {data.totalMb === null ? 'No fresh measurement' : formatMb(data.totalMb)}
              {quota && (
                <span className="text-slate-500 dark:text-slate-400">
                  {' '}
                  / {formatLimitAmount(quota.limit, 'megabytes')}
                </span>
              )}
            </span>
          </div>
          {quota && quota.limit !== null && data.totalMb !== null && (
            <MeterBar value={data.totalMb} max={quota.limit} />
          )}
          {quota && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Overuse policy: {OVERUSE_POLICY_LABELS[quota.policy].label}
              {quota.flagged &&
                ' — this subscription is flagged over quota; enforcement stays a staff decision.'}
            </p>
          )}
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {data.resources.map((resource) => (
              <li key={resource.resourceId} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium">{resource.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {resource.availability === 'pending'
                      ? (resource.reason ?? 'No measurement yet')
                      : (breakdownSummary(resource) ||
                        `measured ${timeAgo(resource.measuredAt!)}`)}
                  </p>
                </div>
                <span className="shrink-0 tabular-nums">
                  {resource.totalMb === null ? (
                    <Badge tone="gray">no data yet</Badge>
                  ) : (
                    formatMb(resource.totalMb)
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/**
 * Per-server disk capacity + collection state (Infrastructure page,
 * ADR-0013). Hidden entirely when the metrics module is disabled.
 */
export function DiskCapacityPanel() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['disk', 'status'],
    queryFn: () => api.get<DiskStatusDto>('/api/metrics/disk/status'),
    retry: false,
    refetchInterval: 60_000,
  });
  const collect = useMutation({
    mutationFn: () => api.post('/api/metrics/disk/collect'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['disk'] }),
  });

  if (status.isPending || status.error) return null;
  const data = status.data;

  return (
    <div className="mt-4">
      <Card>
        <CardHeader
          title="Disk capacity"
          description={`Measured every ${Math.round(data.intervalMs / 60_000)} min over SSH (idle-priority du) · ${data.retentionDays}-day history`}
          actions={
            <Button variant="secondary" loading={collect.isPending} onClick={() => collect.mutate()}>
              <RefreshCw className="size-4" /> Measure now
            </Button>
          }
        />
        <div className="px-5 pb-2">
          {!data.sshKeyConfigured && (
            <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-700 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-500/10 dark:text-amber-400">
              No server-access SSH key configured — disk measurement cannot reach the servers.{' '}
              <Link to="/settings" className="font-medium underline">
                Add it under Settings → Usage metrics
              </Link>
              .
            </p>
          )}
          {collect.error != null && (
            <div className="mt-3">
              <ErrorNote error={collect.error} />
            </div>
          )}
          {data.servers.length === 0 ? (
            <EmptyState
              icon={<HardDrive className="size-10" />}
              title="No measurements yet"
              description="Per-server disk capacity appears after the first measurement run."
            />
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.servers.map((server) => (
                <li key={server.serverUuid} className="space-y-2 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{server.serverName}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {server.resourcesMeasured} resource
                        {server.resourcesMeasured === 1 ? '' : 's'} measured
                        {server.managedMb !== null ? ` · ${formatMb(server.managedMb)} managed` : ''}
                        {server.lastSuccessAt ? ` · measured ${timeAgo(server.lastSuccessAt)}` : ''}
                        {server.durationMs !== null
                          ? ` · took ${Math.max(1, Math.round(server.durationMs / 1000))}s`
                          : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {server.pathsSkipped > 0 && (
                        <Badge tone="yellow">{server.pathsSkipped} skipped</Badge>
                      )}
                      {server.unattributedContainers > 0 && (
                        <Badge tone="gray">{server.unattributedContainers} unattributed</Badge>
                      )}
                    </div>
                  </div>
                  {server.diskTotalMb !== null && server.diskUsedMb !== null && (
                    <div className="space-y-1">
                      <div className="flex items-baseline justify-between text-xs text-slate-500 dark:text-slate-400">
                        <span>Filesystem (Docker data)</span>
                        <span className="tabular-nums">
                          {formatMb(server.diskUsedMb)} of {formatMb(server.diskTotalMb)} used
                          {server.diskAvailMb !== null ? ` · ${formatMb(server.diskAvailMb)} free` : ''}
                        </span>
                      </div>
                      <MeterBar
                        value={server.diskUsedMb}
                        max={server.diskTotalMb}
                        warnAt={0.75}
                        dangerAt={0.9}
                      />
                    </div>
                  )}
                  {server.lastError && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">{server.lastError}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}
